<template>
  <div class="c2c-wd-picker">
    <label :for="inputId">
      {{ t('form.wikidata_label') }}
      <span class="c2c-wd-optional">{{ t('form.wikidata_optional') }}</span>
    </label>
    <small>{{ t('form.wikidata_help') }}</small>

    <!-- Chosen item. The picker holds one item at a time, so the input is
         replaced by the chip rather than sitting beside it. -->
    <div v-if="modelValue" class="c2c-wd-chosen">
      <div class="c2c-wd-chosen-text">
        <a
          :href="wikidataEntityUrl(modelValue.qid)"
          target="_blank"
          rel="noopener noreferrer"
        >{{ modelValue.label }}</a>
        <span class="c2c-wd-qid">{{ modelValue.qid }}</span>
        <small v-if="modelValue.description">{{ modelValue.description }}</small>
      </div>
      <button
        ref="removeEl"
        type="button"
        class="c2c-wd-remove"
        @click="clearItem"
        :aria-label="t('form.wikidata_remove_aria', { name: modelValue.label })"
      ><X :size="16" /></button>
    </div>

    <template v-else>
      <div class="c2c-wd-input-row">
        <input
          :id="inputId"
          ref="inputEl"
          v-model="query"
          type="text"
          role="combobox"
          :placeholder="t('form.wikidata_placeholder')"
          @input="onInput"
          @keydown.enter.prevent="onEnter"
          @keydown.down.prevent="moveDown"
          @keydown.up.prevent="moveUp"
          @keydown.escape="hideSuggestions"
          @blur="onBlur"
          @focus="onFocus"
          autocomplete="off"
          spellcheck="false"
          :aria-expanded="showSuggestions && suggestions.length > 0"
          aria-autocomplete="list"
          :aria-controls="listId"
          :aria-activedescendant="activeIndex >= 0 ? optionId(activeIndex) : undefined"
        />
        <span v-if="loading" class="c2c-wd-loading" aria-hidden="true"></span>
      </div>

      <ul
        v-if="showSuggestions && suggestions.length > 0"
        :id="listId"
        class="c2c-wd-suggestions"
        role="listbox"
      >
        <li
          v-for="(s, i) in suggestions"
          :key="s.qid"
          :id="optionId(i)"
          :class="{ 'c2c-wd-suggestion--active': i === activeIndex }"
          @mousedown.prevent="chooseItem(s)"
          role="option"
          :aria-selected="i === activeIndex"
        >
          <span class="c2c-wd-suggestion-label">{{ s.label }}</span>
          <span class="c2c-wd-qid">{{ s.qid }}</span>
          <small v-if="s.description">{{ s.description }}</small>
        </li>
      </ul>

      <small v-if="noMatches" class="c2c-wd-empty">
        {{ t('form.wikidata_no_matches') }}
      </small>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, useId, nextTick, onBeforeUnmount } from "vue"
import { X } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { searchWikidataItems, isSearchable, wikidataEntityUrl } from "@/lib/wikidata-api"
import type { WikidataItem } from "@/lib/types"

const { t, locale } = useI18n()

// Optional on the form (a form built before this feature simply has no
// such field), so the prop accepts undefined as well as null.
defineProps<{ modelValue?: WikidataItem | null }>()
const emit = defineEmits<{ "update:modelValue": [WikidataItem | null] }>()

// Stable per-instance ids. Math.random() would collide across instances and
// break the label/input association; useId is Vue's own answer to this.
const uid = useId()
const inputId = `c2c-wd-input-${uid}`
const listId = `c2c-wd-list-${uid}`
const optionId = (i: number) => `c2c-wd-opt-${uid}-${i}`

const inputEl = ref<HTMLInputElement | null>(null)
const removeEl = ref<HTMLButtonElement | null>(null)

const query = ref("")
const suggestions = ref<WikidataItem[]>([])
const showSuggestions = ref(false)
const activeIndex = ref(-1)
const loading = ref(false)
const noMatches = ref(false)

let debounceTimer: ReturnType<typeof setTimeout> | undefined
// Bumped by every keystroke, choice, clear and unmount. A search may only
// touch the dropdown if the number it started with is still current - which
// is what stops a slow response for "Taj" landing after the user has typed
// "Taj Mahal", emptied the box, or already picked an item.
let searchSeq = 0
// The request on the wire, so a superseded one is cancelled rather than left
// to finish. That also keeps the two requests of a bare-number lookup from
// piling up against Wikidata's rate limit while someone is still typing.
let inFlight: AbortController | null = null

/**
 * Invalidate any pending or running search. Always clears the spinner: an
 * invalidated search never clears it itself, so this is the one place that
 * guarantees the picker cannot be left looking busy.
 */
function cancelSearch() {
  clearTimeout(debounceTimer)
  debounceTimer = undefined
  searchSeq++
  inFlight?.abort()
  inFlight = null
  loading.value = false
}

function resetSearchUi() {
  query.value = ""
  suggestions.value = []
  showSuggestions.value = false
  activeIndex.value = -1
  noMatches.value = false
}

function onInput() {
  cancelSearch()
  activeIndex.value = -1
  noMatches.value = false
  // The rule lives in wikidata-api.ts so the picker and the search can never
  // disagree about what is worth sending.
  if (!isSearchable(query.value)) {
    suggestions.value = []
    showSuggestions.value = false
    return
  }
  loading.value = true
  const seq = searchSeq
  debounceTimer = setTimeout(async () => {
    const controller = new AbortController()
    inFlight = controller
    let results: WikidataItem[] = []
    try {
      results = await searchWikidataItems(
        query.value.trim(), String(locale.value || "en"), controller.signal)
    } catch (err) {
      // searchWikidataItems is contracted never to reject; this is the net
      // under that contract, so a rejection can neither escape unhandled nor
      // strand the spinner.
      console.warn("[c2c] Wikidata search failed:", err)
    }
    if (seq !== searchSeq) return
    inFlight = null
    suggestions.value = results
    showSuggestions.value = results.length > 0
    noMatches.value = results.length === 0
    loading.value = false
  }, 250)
}

function onEnter() {
  if (activeIndex.value >= 0 && activeIndex.value < suggestions.value.length) {
    chooseItem(suggestions.value[activeIndex.value])
  }
  // Deliberately does nothing otherwise: unlike a category, a Q-id cannot be
  // typed freehand and be meaningful, so there is nothing to accept.
}

function moveDown() {
  if (suggestions.value.length === 0) return
  activeIndex.value = Math.min(activeIndex.value + 1, suggestions.value.length - 1)
}
function moveUp() {
  if (suggestions.value.length === 0) return
  activeIndex.value = Math.max(activeIndex.value - 1, -1)
}

function chooseItem(item: WikidataItem) {
  cancelSearch()
  // Only the item travels. Which property the statement uses (P10 / P51) is
  // decided at upload time from the media mode, so switching to audio-only
  // after picking is still honoured.
  emit("update:modelValue", { qid: item.qid, label: item.label, description: item.description })
  resetSearchUi()
  // The input that had focus has just been replaced by the chip.
  void nextTick(() => removeEl.value?.focus())
}

function clearItem() {
  cancelSearch()
  emit("update:modelValue", null)
  resetSearchUi()
  // The remove button that had focus is gone; give focus back to the search.
  void nextTick(() => inputEl.value?.focus())
}

function onBlur() { setTimeout(() => { showSuggestions.value = false }, 150) }
function onFocus() { if (suggestions.value.length > 0) showSuggestions.value = true }
function hideSuggestions() { showSuggestions.value = false; activeIndex.value = -1 }

onBeforeUnmount(cancelSearch)
</script>

<style scoped>
.c2c-wd-picker { display: flex; flex-direction: column; gap: 4px; position: relative; }
.c2c-wd-picker > label { font-weight: 600; font-size: 14px; }
.c2c-wd-optional { font-weight: 400; color: #54595d; font-size: 12px; margin-inline-start: 6px; }
.c2c-wd-picker > small { color: #54595d; font-size: 12px; }

.c2c-wd-input-row { position: relative; }
.c2c-wd-input-row input {
  width: 100%; padding: 8px 32px 8px 10px;
  border: 1px solid #a2a9b1; border-radius: 4px;
  font: inherit; box-sizing: border-box;
}
.c2c-wd-input-row input:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-wd-loading {
  position: absolute; right: 12px; top: 50%;
  width: 16px; height: 16px; margin-top: -8px;
  box-sizing: border-box;
  border: 2px solid var(--border-color-subtle, #c8ccd1);
  border-top-color: var(--background-color-progressive, #36c);
  border-radius: 50%; animation: c2c-wd-spin 0.8s linear infinite;
}
@keyframes c2c-wd-spin { to { transform: rotate(360deg); } }

.c2c-wd-suggestions {
  position: absolute; top: 100%; inset-inline: 0;
  margin: 2px 0 0 0; padding: 4px 0; list-style: none;
  background: white; border: 1px solid #a2a9b1; border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  max-height: 260px; overflow-y: auto; z-index: 10;
}
.c2c-wd-suggestions li {
  padding: 8px 12px; cursor: pointer; font-size: 14px;
  display: flex; flex-direction: column; gap: 2px;
}
.c2c-wd-suggestions li:hover, .c2c-wd-suggestion--active { background: #eaf3ff; }
.c2c-wd-suggestion-label { font-weight: 600; }
.c2c-wd-suggestions small { color: #54595d; font-size: 12px; }
.c2c-wd-qid { color: #72777d; font-size: 11px; font-family: monospace; }

.c2c-wd-chosen {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 8px 8px 12px;
  background: #eaf3ff; border: 1px solid #c8d6f0; border-radius: 4px;
}
.c2c-wd-chosen-text { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.c2c-wd-chosen-text a { font-weight: 600; color: #3366cc; }
.c2c-wd-chosen-text small { color: #54595d; font-size: 12px; }
/* Was a text times-sign in a 2px by 8px box with border-radius 50%, which
   renders an ellipse, not a circle. Fixed square box, Codex icon. */
.c2c-wd-remove {
  background: none; border: none; cursor: pointer;
  color: #54595d;
  flex: 0 0 auto;
  width: 32px; height: 32px; padding: 0;
  /* Same shape as every other dismiss control in the form. */
  border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center;
}
.c2c-wd-remove :deep(svg) { width: 14px; height: 14px; display: block; }
/* Remove is destructive, so hovering it turns red wherever it appears.
   Inside a tinted chip the plain grey hover gave no sign that the
   click would delete anything. */
.c2c-wd-remove:hover { background: #fcd2d2; color: #b32424; }
.c2c-wd-remove:focus-visible { outline: 2px solid #3366cc; outline-offset: 1px; }
.c2c-wd-empty { color: #54595d; font-size: 12px; }
</style>
